import { ICompassusDashboardRepository } from "../../adapters/driven/repositories/compassusDashboardRepository";
import {
  ICompassusMonthlyAssumption,
  ICreateCompassusMonthlyAssumptionPayload,
  ICreateCompassusMonthlyRecordPayload,
  ICreateCompassusPtoRecordPayload,
  IGetCompassusMonthlyAssumptionsParams,
  IGetCompassusMonthlyAssumptionsResult,
  IGetCompassusMonthlyRecordsParams,
  IGetCompassusMonthlyRecordsResult,
  IGetCompassusPtoRecordsParams,
  IGetCompassusPtoRecordsResult,
  IGetEngagementUtilizationParams,
  IGetEngagementUtilizationResult,
  IGetNasaTlxFeedbackParams,
  IGetNasaTlxFeedbackResult,
  IGetProductivityParams,
  IGetProductivityProvidersResult,
  IGetProductivityResult,
  IGetProductivitySummaryResult,
  IGetProductivityUtilizationResult,
  IMutateCompassusMonthlyAssumptionResult,
  IMutateCompassusMonthlyRecordResult,
  IMutateCompassusProviderProfileResult,
  IMutateCompassusPtoRecordResult,
  IUpdateCompassusMonthlyAssumptionPayload,
  IUpdateCompassusMonthlyRecordPayload,
  IUpdateCompassusProviderProfilePayload,
  IUpdateCompassusPtoRecordPayload,
} from "../ports/driver/compassusDashboardTypes";
import {
  IGetFacilityNpPatientsParams,
  IGetFacilityNpPatientsResult,
  IGetNpPatientsParams,
  IGetNpPatientsResult,
  IGetNpProductivityMeParams,
  IGetNpProductivityMeResult,
  IGetNpScheduleParams,
  IGetNpScheduleResult,
} from "../ports/driver/compassusNpDashboardTypes";

/**
 * The API provider resolves error responses with the error body instead of
 * throwing, so the status code is unavailable here. The NP endpoints reject
 * users without an active NP employee profile with a 403 `{ detail }` body —
 * since the UI always sends well-formed windows (no 422s in practice), a
 * `detail`-only body is treated as "no NP access".
 */
const isNoNpAccessBody = (data: unknown): boolean =>
  data != null &&
  typeof data === "object" &&
  "detail" in (data as Record<string, unknown>);

const normalizeMonthlyAssumptionsResponse = (
  data: unknown
): { assumptions: ICompassusMonthlyAssumption[] } | null => {
  if (Array.isArray(data)) {
    return { assumptions: data as ICompassusMonthlyAssumption[] };
  }

  if (data == null || typeof data !== "object") {
    return null;
  }

  const body = data as Record<string, unknown>;
  if (Array.isArray(body.assumptions)) {
    return {
      assumptions: body.assumptions as ICompassusMonthlyAssumption[],
    };
  }

  if (Array.isArray(body.records)) {
    return { assumptions: body.records as ICompassusMonthlyAssumption[] };
  }

  return null;
};

export const CompassusDashboardServiceImplFactory = ({
  compassusDashboardRepository,
}: {
  compassusDashboardRepository: ICompassusDashboardRepository;
}) => {
  const getProductivity = async (
    params: IGetProductivityParams,
    signal?: AbortSignal
  ): Promise<IGetProductivityResult> => {
    try {
      const data = await compassusDashboardRepository.getProductivity(
        params,
        signal
      );
      // The API provider resolves error responses (e.g. 422 on a malformed
      // month window) with the error body instead of throwing — detect them
      // by the missing months array and surface them as failures.
      if (!data || !Array.isArray(data.months)) {
        return { success: false, error: data };
      }
      return { success: true, data };
    } catch (error) {
      return { success: false, error };
    }
  };

  const getProductivitySummary = async (
    params: IGetProductivityParams,
    signal?: AbortSignal
  ): Promise<IGetProductivitySummaryResult> => {
    try {
      const data = await compassusDashboardRepository.getProductivitySummary(
        params,
        signal
      );
      if (!data || !Array.isArray(data.months) || !data.aggregate) {
        return { success: false, error: data };
      }
      return { success: true, data };
    } catch (error) {
      return { success: false, error };
    }
  };

  const getProductivityProviders = async (
    params: IGetProductivityParams,
    signal?: AbortSignal
  ): Promise<IGetProductivityProvidersResult> => {
    try {
      const data = await compassusDashboardRepository.getProductivityProviders(
        params,
        signal
      );
      if (!data || !Array.isArray(data.providers)) {
        return { success: false, error: data };
      }
      return { success: true, data };
    } catch (error) {
      return { success: false, error };
    }
  };

  const getProductivityUtilization = async (
    params: IGetProductivityParams,
    signal?: AbortSignal
  ): Promise<IGetProductivityUtilizationResult> => {
    try {
      const data =
        await compassusDashboardRepository.getProductivityUtilization(
          params,
          signal
        );
      const hasNasaTlx =
        !!data?.nasa_tlx &&
        Array.isArray(data.nasa_tlx.by_dimension) &&
        Array.isArray(data.nasa_tlx.clinics);
      const hasInactiveUsers = Array.isArray(
        data?.weekly_inactive_users?.users
      );
      if (!data || (!hasNasaTlx && !hasInactiveUsers)) {
        return { success: false, error: data };
      }
      return { success: true, data };
    } catch (error) {
      return { success: false, error };
    }
  };

  const getEngagementUtilization = async (
    params: IGetEngagementUtilizationParams,
    signal?: AbortSignal
  ): Promise<IGetEngagementUtilizationResult> => {
    try {
      const data = await compassusDashboardRepository.getEngagementUtilization(
        params,
        signal
      );
      if (
        !data ||
        !data.engagement_utilization ||
        !Array.isArray(data.engagement_utilization.daily)
      ) {
        return { success: false, error: data };
      }
      return { success: true, data };
    } catch (error) {
      return { success: false, error };
    }
  };

  const getPtoRecords = async (
    params: IGetCompassusPtoRecordsParams,
    signal?: AbortSignal
  ): Promise<IGetCompassusPtoRecordsResult> => {
    try {
      const data = await compassusDashboardRepository.getPtoRecords(
        params,
        signal
      );
      if (!data || !Array.isArray(data.records)) {
        return { success: false, error: data };
      }
      return { success: true, data };
    } catch (error) {
      return { success: false, error };
    }
  };

  const createPtoRecord = async (
    payload: ICreateCompassusPtoRecordPayload
  ): Promise<IMutateCompassusPtoRecordResult> => {
    try {
      const data = await compassusDashboardRepository.createPtoRecord(payload);
      if (!data || typeof data.id !== "number") {
        return { success: false, error: data };
      }
      return { success: true, data };
    } catch (error) {
      return { success: false, error };
    }
  };

  const updatePtoRecord = async (
    ptoRecordId: number,
    payload: IUpdateCompassusPtoRecordPayload
  ): Promise<IMutateCompassusPtoRecordResult> => {
    try {
      const data = await compassusDashboardRepository.updatePtoRecord(
        ptoRecordId,
        payload
      );
      if (!data || typeof data.id !== "number") {
        return { success: false, error: data };
      }
      return { success: true, data };
    } catch (error) {
      return { success: false, error };
    }
  };

  const getMonthlyRecords = async (
    params: IGetCompassusMonthlyRecordsParams,
    signal?: AbortSignal
  ): Promise<IGetCompassusMonthlyRecordsResult> => {
    try {
      const data = await compassusDashboardRepository.getMonthlyRecords(
        params,
        signal
      );
      if (!data || !Array.isArray(data.records)) {
        return { success: false, error: data };
      }
      return { success: true, data };
    } catch (error) {
      return { success: false, error };
    }
  };

  const createMonthlyRecord = async (
    payload: ICreateCompassusMonthlyRecordPayload
  ): Promise<IMutateCompassusMonthlyRecordResult> => {
    try {
      const data = await compassusDashboardRepository.createMonthlyRecord(
        payload
      );
      if (!data || typeof data.id !== "number") {
        return { success: false, error: data };
      }
      return { success: true, data };
    } catch (error) {
      return { success: false, error };
    }
  };

  const updateMonthlyRecord = async (
    monthlyRecordId: number,
    payload: IUpdateCompassusMonthlyRecordPayload
  ): Promise<IMutateCompassusMonthlyRecordResult> => {
    try {
      const data = await compassusDashboardRepository.updateMonthlyRecord(
        monthlyRecordId,
        payload
      );
      if (!data || typeof data.id !== "number") {
        return { success: false, error: data };
      }
      return { success: true, data };
    } catch (error) {
      return { success: false, error };
    }
  };

  const getMonthlyAssumptions = async (
    params: IGetCompassusMonthlyAssumptionsParams,
    signal?: AbortSignal
  ): Promise<IGetCompassusMonthlyAssumptionsResult> => {
    try {
      const data = await compassusDashboardRepository.getMonthlyAssumptions(
        params,
        signal
      );
      const normalized = normalizeMonthlyAssumptionsResponse(data);
      if (!normalized) {
        return { success: false, error: data };
      }
      return { success: true, data: normalized };
    } catch (error) {
      return { success: false, error };
    }
  };

  const createMonthlyAssumption = async (
    payload: ICreateCompassusMonthlyAssumptionPayload
  ): Promise<IMutateCompassusMonthlyAssumptionResult> => {
    try {
      const data = await compassusDashboardRepository.createMonthlyAssumption(
        payload
      );
      if (!data || typeof data.id !== "number") {
        return { success: false, error: data };
      }
      return { success: true, data };
    } catch (error) {
      return { success: false, error };
    }
  };

  const updateMonthlyAssumption = async (
    assumptionId: number,
    payload: IUpdateCompassusMonthlyAssumptionPayload
  ): Promise<IMutateCompassusMonthlyAssumptionResult> => {
    try {
      const data = await compassusDashboardRepository.updateMonthlyAssumption(
        assumptionId,
        payload
      );
      if (!data || typeof data.id !== "number") {
        return { success: false, error: data };
      }
      return { success: true, data };
    } catch (error) {
      return { success: false, error };
    }
  };

  const updateProviderProfile = async (
    providerUserId: number,
    payload: IUpdateCompassusProviderProfilePayload
  ): Promise<IMutateCompassusProviderProfileResult> => {
    try {
      const data = await compassusDashboardRepository.updateProviderProfile(
        providerUserId,
        payload
      );
      if (!data || typeof data !== "object" || "detail" in data) {
        return { success: false, error: data };
      }
      return { success: true, data };
    } catch (error) {
      return { success: false, error };
    }
  };

  const getMyProductivity = async (
    params: IGetNpProductivityMeParams,
    signal?: AbortSignal
  ): Promise<IGetNpProductivityMeResult> => {
    try {
      const data = await compassusDashboardRepository.getMyProductivity(
        params,
        signal
      );
      if (!data || !Array.isArray(data.months)) {
        return {
          success: false,
          noNpAccess: isNoNpAccessBody(data),
          error: data,
        };
      }
      return { success: true, data };
    } catch (error) {
      return { success: false, noNpAccess: false, error };
    }
  };

  const getMySchedule = async (
    params: IGetNpScheduleParams,
    signal?: AbortSignal
  ): Promise<IGetNpScheduleResult> => {
    try {
      const data = await compassusDashboardRepository.getMySchedule(
        params,
        signal
      );
      if (!data || !Array.isArray(data.visits)) {
        return {
          success: false,
          noNpAccess: isNoNpAccessBody(data),
          error: data,
        };
      }
      return { success: true, data };
    } catch (error) {
      return { success: false, noNpAccess: false, error };
    }
  };

  const getMyPatients = async (
    params: IGetNpPatientsParams,
    signal?: AbortSignal
  ): Promise<IGetNpPatientsResult> => {
    try {
      const data = await compassusDashboardRepository.getMyPatients(
        params,
        signal
      );
      if (!data || !Array.isArray(data.patients)) {
        return {
          success: false,
          noNpAccess: isNoNpAccessBody(data),
          error: data,
        };
      }
      return { success: true, data };
    } catch (error) {
      return { success: false, noNpAccess: false, error };
    }
  };

  const getNasaTlxFeedback = async (
    params: IGetNasaTlxFeedbackParams,
    signal?: AbortSignal
  ): Promise<IGetNasaTlxFeedbackResult> => {
    try {
      const data = await compassusDashboardRepository.getNasaTlxFeedback(
        params,
        signal
      );
      if (!data || !Array.isArray(data.users)) {
        return { success: false, error: data };
      }
      return { success: true, data };
    } catch (error) {
      return { success: false, error };
    }
  };

  const getFacilityNpPatients = async (
    params: IGetFacilityNpPatientsParams,
    signal?: AbortSignal
  ): Promise<IGetFacilityNpPatientsResult> => {
    try {
      const data = await compassusDashboardRepository.getFacilityNpPatients(
        params,
        signal
      );
      if (!data || !Array.isArray(data.patients)) {
        return { success: false, error: data };
      }
      return { success: true, data };
    } catch (error) {
      return { success: false, error };
    }
  };

  return {
    getProductivity,
    getProductivitySummary,
    getProductivityProviders,
    getProductivityUtilization,
    getEngagementUtilization,
    getPtoRecords,
    createPtoRecord,
    updatePtoRecord,
    getMonthlyRecords,
    createMonthlyRecord,
    updateMonthlyRecord,
    getMonthlyAssumptions,
    createMonthlyAssumption,
    updateMonthlyAssumption,
    updateProviderProfile,
    getMyProductivity,
    getMySchedule,
    getMyPatients,
    getNasaTlxFeedback,
    getFacilityNpPatients,
  };
};

export type ICompassusDashboardService = ReturnType<
  typeof CompassusDashboardServiceImplFactory
>;
